import { useEffect } from 'react'
import type { FormInstance } from 'antd/es/form/Form'
import {
  getIndividualLessonLinkClearedFields,
  getIndividualLessonLinkFieldDefaults,
  getIndividualLessonLinkTotal,
  isIndividualLessonLinkTariff,
} from './components/IndividualLessonLinkFields/individualLessonLink.utils'
import { getGroupPerLessonLinkFieldDefaults } from './components/GroupPerLessonLinkFields/groupPerLessonLink.utils'
import {
  DEFAULT_MONTHLY_COURSE_PERIOD_UNIT,
  getClassLinkSuggestedPrice,
  getMonthlyCourseLinkSuggestedPrice,
  isMonthlyCourseLinkTariff,
} from './components/MonthlyCourseLinkFields/monthlyCourseLink.utils'
import { getStreamCustomPeriodSuggestedPrice } from './components/StreamCustomPeriodLinkFields/streamCustomPeriodLink.utils'
import { getStreamOneTimeDefaultPrice } from './components/StreamOneTimeLinkFields/streamOneTimeLink.utils'
import { getTariffValueMin } from '@pages/CoursesCatalog/modals/CourseEnrollmentModal/components/SelectTariffValue/SelectTariffValue'
import {
  isCourseLinkGroupSelectableTariff,
  isGroupPerLessonLinkTariff,
  isLessonPaymentTariff,
  isStreamCustomPeriodTariff,
} from './tariffTypes'

type UseCreateLinkFormResetsParams = {
  form: FormInstance
  open: boolean
  typeWatch: string | null | undefined
  courseIdWatch: number | null
  selectedTariff?: ICourseTariff | null
  selectedClass?: {
    id?: number
    amount?: unknown
    one_pay?: boolean | number | null
  } | null
  isOneTimeActive: boolean
  setIsCustomPrice: (value: boolean) => void
  setIsPeriodFixed: (value: boolean) => void
  setMonthlyPriceManuallyEdited: (value: boolean) => void
  setIndividualPriceManuallyEdited: (value: boolean) => void
}

/**
 * Сбросы формы create-link при смене курса/тарифа/класса/типа/закрытии.
 * Поведение перенесено из CreateLinkModal без изменений.
 */
export const useCreateLinkFormResets = ({
  form,
  open,
  typeWatch,
  courseIdWatch,
  selectedTariff,
  selectedClass,
  isOneTimeActive,
  setIsCustomPrice,
  setIsPeriodFixed,
  setMonthlyPriceManuallyEdited,
  setIndividualPriceManuallyEdited,
}: UseCreateLinkFormResetsParams) => {
  useEffect(
    () =>
      form.setFieldsValue({
        tariff_id: null,
        value: null,
        is_value_fixed: false,
        enroll_date: null,
        price: null,
        first_price: null,
        ...getIndividualLessonLinkClearedFields(),
        discount: null,
        discount_mode: null,
        apply_discount_each_payment: false,
        period_unit: DEFAULT_MONTHLY_COURSE_PERIOD_UNIT,
        group_id: null,
      }),
    [courseIdWatch, form],
  )

  useEffect(() => {
    if (!selectedTariff) return

    const individualDefaults = isIndividualLessonLinkTariff(selectedTariff.type)
      ? getIndividualLessonLinkFieldDefaults(selectedTariff)
      : isGroupPerLessonLinkTariff(selectedTariff.type)
      ? getGroupPerLessonLinkFieldDefaults(selectedTariff)
      : {
          first_price: null,
          ...getIndividualLessonLinkClearedFields(),
        }

    const isOneTime = isOneTimeActive
    const isMonthly = isMonthlyCourseLinkTariff(selectedTariff.type)
    const isStreamCustom = isStreamCustomPeriodTariff(selectedTariff.type)
    const isLesson = isLessonPaymentTariff(selectedTariff.type)
    const monthlyDefaultValue = 1
    const lessonDefaultValue = 1
    const streamDefaultValue = getTariffValueMin(selectedTariff) || 1

    const nextValue =
      selectedTariff.type === 'free' || isOneTime
        ? null
        : isMonthly
        ? monthlyDefaultValue
        : isLesson
        ? lessonDefaultValue
        : streamDefaultValue

    // Сразу кладём расчётную цену — иначе sync-effect другого тарифа
    // успевает перезаписать form.price в null/0 (инпут показывает 0).
    const nextPrice = isOneTime
      ? getStreamOneTimeDefaultPrice(selectedTariff)
      : isMonthly
      ? getMonthlyCourseLinkSuggestedPrice(
          selectedTariff,
          DEFAULT_MONTHLY_COURSE_PERIOD_UNIT,
          monthlyDefaultValue,
          null,
          null,
          false,
        )
      : isStreamCustom
      ? getStreamCustomPeriodSuggestedPrice(
          selectedTariff,
          streamDefaultValue,
          null,
        )
      : isLesson
      ? getIndividualLessonLinkTotal(
          individualDefaults.first_price,
          lessonDefaultValue,
        )
      : null

    form.setFieldsValue({
      value: nextValue,
      is_value_fixed: false,
      period_unit: DEFAULT_MONTHLY_COURSE_PERIOD_UNIT,
      enroll_date: null,
      ...individualDefaults,
      price: nextPrice,
      discount: null,
      discount_mode: null,
      apply_discount_each_payment: false,
    })
    setIsCustomPrice(false)
    setIsPeriodFixed(false)
    setMonthlyPriceManuallyEdited(false)
    setIndividualPriceManuallyEdited(false)
    if (!isCourseLinkGroupSelectableTariff(selectedTariff.type)) {
      form.setFieldValue('group_id', null)
    }
    // deps как в CreateLinkModal: только смена тарифа (id/type)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, selectedTariff?.id, selectedTariff?.type])

  useEffect(() => {
    if (typeWatch === 'class') {
      form.setFieldsValue({
        discount: null,
        discount_mode: null,
        apply_discount_each_payment: false,
        group_id: null,
        ...getIndividualLessonLinkClearedFields(),
      })
    }
  }, [form, typeWatch])

  useEffect(() => {
    form.setFieldsValue({
      price: selectedClass?.one_pay
        ? getClassLinkSuggestedPrice(
            selectedClass.amount,
            DEFAULT_MONTHLY_COURSE_PERIOD_UNIT,
            1,
            null,
            form.getFieldValue('enroll_date'),
          )
        : null,
      first_price: selectedClass?.amount ?? null,
      value: selectedClass?.one_pay ? 1 : null,
      period_unit: DEFAULT_MONTHLY_COURSE_PERIOD_UNIT,
      discount: null,
      discount_mode: null,
      apply_discount_each_payment: false,
    })
    setIsPeriodFixed(false)
    setMonthlyPriceManuallyEdited(false)
    setIndividualPriceManuallyEdited(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, selectedClass?.id, selectedClass?.amount, selectedClass?.one_pay])

  useEffect(() => {
    if (!open) {
      form.resetFields()
      setIsCustomPrice(false)
      setIsPeriodFixed(false)
      setMonthlyPriceManuallyEdited(false)
      setIndividualPriceManuallyEdited(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, open])
}
